import type { ReactThemeCustomThemeConfig } from './types';
import { hexToRgba } from './utils';

const colorPrimaryDefault = '#4211A0';
const colorErrorDefault = '#EA0B16';
const colorBaseDefault = '#E8E8E8';

export const reactThemeCustomThemeConfig: ReactThemeCustomThemeConfig = {
  border: {
    base: `1px solid ${colorBaseDefault}`,
    radius: {
      base: { unit: 'px', value: 4 },
      lg: { unit: 'px', value: 8 },
      sm: { unit: 'px', value: 2 },
      xl: { unit: 'px', value: 16 },
    },
  },
  color: {
    accent: {
      default: '#FACA22',
    },
    blue: {
      background: '#E6F7FF',
      default: '#002766',
    },
    border: {
      default: colorBaseDefault,
    },
    // TODO: this is yellow in figma
    caution: {
      background: '#FEFBE7',
      default: '#FADB14',
    },
    cyan: {
      background: '#E6FFFB',
      default: '#002329',
    },
    error: {
      background: '#FFE3E3',
      default: colorErrorDefault,
      default80: hexToRgba({ hex: colorErrorDefault, opacity: 0.8 }),
    },
    green: {
      background: '#F6FFED',
      default: '#092B00',
    },
    info: {
      background: '#E5F0FF',
      default: '#006AFF',
    },
    orangeDark: {
      background: '#FFF2E8',
      default: '#610B00',
    },
    orangeLight: {
      background: '#FCF1E6',
      default: '#DF7402',
    },
    pink: {
      background: '#FFF0F6',
      default: '#520339',
    },
    primary: {
      background: '#EBE6F5',
      default: colorPrimaryDefault,
      default10: hexToRgba({ hex: colorPrimaryDefault, opacity: 0.1 }),
      default6: hexToRgba({ hex: colorPrimaryDefault, opacity: 0.06 }),
      default80: hexToRgba({ hex: colorPrimaryDefault, opacity: 0.8 }),
      subtle: '#F4F1F9',
    },
    purple: {
      background: '#F9F0FF',
      default: '#120338',
    },
    success: {
      background: '#EBFEE0',
      default: '#509E2A',
    },
    surface: {
      dark: '#201830',
      default: '#FFFFFF',
      disabled: '#F7F7F7',
      subtle: '#F6F6F6',
    },
    text: {
      default: '#595959',
      disabled: '#B8B8B8',
      heading: '#262626',
      placeholder: '#BFBFBF',
      secondary: '#8C8C8C',
    },
    warning: {
      background: '#FFECD5',
      default: '#D96812',
    },
  },
  fontFamily: '"Roboto", "Open Sans", sans-serif',
  fontSize: {
    content: {
      base: { unit: 'px', value: 14 },
      lg: { unit: 'px', value: 16 },
      sm: { unit: 'px', value: 12 },
    },
    heading: {
      h1: { unit: 'px', value: 30 },
      h2: { unit: 'px', value: 24 },
      h3: { unit: 'px', value: 20 },
      h4: { unit: 'px', value: 16 },
      h5: { unit: 'px', value: 12 },
    },
    text: {
      base: { unit: 'px', value: 14 },
      lg: { unit: 'px', value: 16 },
      sm: { unit: 'px', value: 12 },
    },
  },
  fontWeight: {
    base: 400,
    bold: 700,
    medium: 500,
  },
  outline: {
    offset: { unit: 'px', value: 1 },
    width: { unit: 'px', value: 2 },
  },
  shadow: {
    danger: 'none',
    default: 'none',
    primary: 'none',
  },
  spacing: {
    lg: { unit: 'px', value: 24 },
    md: { unit: 'px', value: 20 },
    ms: { unit: 'px', value: 16 },
    sm: { unit: 'px', value: 12 },
    xl: { unit: 'px', value: 32 },
    xs: { unit: 'px', value: 8 },
    xxl: { unit: 'px', value: 48 },
    xxs: { unit: 'px', value: 4 },
    xxxs: { unit: 'px', value: 2 },
  },
};
